import React, { useState, useEffect } from 'react';
import { X, AlertCircle, CheckCircle } from 'lucide-react';

// The pharmacy rejects any sig (directions) longer than this with a 400. Mirror
// the backend limit (MAX_PHARMACY_SIG_LENGTH in backend/src/utils/sigUnits.ts) so
// physicians are blocked here instead of having the order silently fail to transmit.
const MAX_SIG_LENGTH = 150;

interface Product {
  _id: string;
  drugName: string;
  concentration: string;
  format: string;
  size: string;
  displayName: string;
  description?: string;
  categories: string[];
  basePrice: number;
  allowPerUnitOrdering: boolean;
  perUnitPrice?: number;
  estimatedShipDays: number;
  defaultSig?: string;
  sigOptions?: string[];
  calculatedPrice?: {
    finalPrice: number;
  };
}

interface PrescriptionData {
  productId: string;
  quantity: number;
  sig: string; // Prescription instructions
  pharmacyNotes: string;
  daysSupply: number;
}

interface PrescriptionFormProps {
  product: Product;
  onSave: (prescription: PrescriptionData) => void;
  onCancel: () => void;
  initialData?: PrescriptionData;
  asModal?: boolean; // New prop to control modal rendering
  savedSigs?: string[]; // Physician's own saved sigs for this product (shown first in dropdown)
  onSaveSigDefault?: (sig: string) => void; // Called on submit when "save this sig" is checked
}

export const PrescriptionForm: React.FC<PrescriptionFormProps> = ({
  product,
  onSave,
  onCancel,
  initialData,
  asModal = true, // Default to true for backward compatibility
  savedSigs = [],
  onSaveSigDefault
}) => {
  // Physician's saved sigs come first; admin presets follow (minus any duplicates)
  const adminSigOptions = (product.sigOptions || []).filter(opt => !savedSigs.includes(opt));
  const allSigOptions = [...savedSigs, ...adminSigOptions];
  const hasSigOptions = allSigOptions.length > 0;
  const getInitialSigSelection = () => {
    if (initialData?.sig) {
      // If editing, check if current sig matches a preset option
      if (allSigOptions.includes(initialData.sig)) {
        return initialData.sig;
      }
      return '__custom__';
    }
    // New prescription: default to first saved/preset sig if available
    if (hasSigOptions) return allSigOptions[0];
    return '__custom__';
  };

  const [sigSelection, setSigSelection] = useState(getInitialSigSelection);
  const [saveSigChecked, setSaveSigChecked] = useState(false);
  const [formData, setFormData] = useState<PrescriptionData>({
    productId: product._id,
    quantity: initialData?.quantity || 1,
    sig: initialData?.sig || (hasSigOptions ? allSigOptions[0] : ''),
    pharmacyNotes: initialData?.pharmacyNotes || '',
    daysSupply: initialData?.daysSupply || 30
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    console.log('PrescriptionForm mounted/updated for product:', product.displayName);
    return () => {
      console.log('PrescriptionForm unmounting for product:', product.displayName);
    };
  }, [product.displayName]);

  useEffect(() => {
    console.log('PrescriptionForm formData changed:', formData);
  }, [formData]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.sig.trim()) {
      newErrors.sig = 'Prescription instructions (sig) are required';
    } else if (formData.sig.trim().length > MAX_SIG_LENGTH) {
      newErrors.sig = `Instructions must be ${MAX_SIG_LENGTH} characters or fewer (currently ${formData.sig.trim().length}). The pharmacy will reject longer directions.`;
    }

    if (formData.quantity <= 0) {
      newErrors.quantity = 'Quantity must be greater than 0';
    }

    if (formData.daysSupply <= 0) {
      newErrors.daysSupply = 'Days supply must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof PrescriptionData, value: any) => {
    console.log('PrescriptionForm handleInputChange:', field, value);
    setFormData(prev => {
      console.log('PrescriptionForm formData update:', prev, { [field]: value });
      return {
        ...prev,
        [field]: value
      };
    });
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('PrescriptionForm handleSubmit:', formData);
    if (validateForm()) {
      const sig = formData.sig.trim();
      if (saveSigChecked && onSaveSigDefault && sig && !savedSigs.includes(sig)) {
        onSaveSigDefault(sig);
      }
      onSave(formData);
    }
  };

  const displayPrice = product.calculatedPrice?.finalPrice || product.basePrice;

  // Form content
  const formContent = (
    <form id="prescription-form" onSubmit={handleSubmit} className="space-y-6">
      {/* Product Info */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="font-medium text-gray-900 mb-2">{product.displayName}</h3>
        <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
          <div>
            <span className="font-medium">Drug Name:</span> {product.drugName}
          </div>
          <div>
            <span className="font-medium">Concentration:</span> {product.concentration}
          </div>
          <div>
            <span className="font-medium">Format:</span> {product.format}
          </div>
          <div>
            <span className="font-medium">Size:</span> {product.size}
          </div>
          <div>
            <span className="font-medium">Price:</span> ${displayPrice.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Prescription Instructions (Sig) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Prescription Instructions (Sig) <span className="text-red-500">*</span>
        </label>
        {hasSigOptions && (
          <select
            value={sigSelection}
            onChange={(e) => {
              const value = e.target.value;
              setSigSelection(value);
              if (value !== '__custom__') {
                handleInputChange('sig', value);
              } else {
                handleInputChange('sig', '');
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
          >
            {savedSigs.length > 0 ? (
              <>
                <optgroup label="My saved sigs">
                  {savedSigs.map((opt, i) => (
                    <option key={`saved-${i}`} value={opt}>{opt}</option>
                  ))}
                </optgroup>
                {adminSigOptions.length > 0 && (
                  <optgroup label="Product presets">
                    {adminSigOptions.map((opt, i) => (
                      <option key={`preset-${i}`} value={opt}>{opt}</option>
                    ))}
                  </optgroup>
                )}
              </>
            ) : (
              adminSigOptions.map((opt, i) => (
                <option key={i} value={opt}>{opt}</option>
              ))
            )}
            <option value="__custom__">Custom...</option>
          </select>
        )}
        {(!hasSigOptions || sigSelection === '__custom__') && (
          <textarea
            value={formData.sig}
            onChange={(e) => handleInputChange('sig', e.target.value)}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${
              errors.sig ? 'border-red-500' : 'border-gray-300'
            }`}
            rows={3}
            placeholder="e.g., Take 1 tablet by mouth daily as needed for pain"
          />
        )}
        {formData.sig.length > 0 && (
          <p className={`mt-1 text-xs text-right ${
            formData.sig.trim().length > MAX_SIG_LENGTH ? 'text-red-600 font-medium' : 'text-gray-400'
          }`}>
            {formData.sig.trim().length}/{MAX_SIG_LENGTH} characters
          </p>
        )}
        {sigSelection === '__custom__' && onSaveSigDefault && formData.sig.trim() && !savedSigs.includes(formData.sig.trim()) && (
          <label className="mt-2 flex items-center text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={saveSigChecked}
              onChange={(e) => setSaveSigChecked(e.target.checked)}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2"
            />
            Save this sig as a default for {product.displayName}
          </label>
        )}
        {errors.sig && (
          <p className="mt-1 text-sm text-red-600 flex items-center">
            <AlertCircle className="h-4 w-4 mr-1" />
            {errors.sig}
          </p>
        )}
      </div>

      {/* Quantity and Days Supply */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quantity <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min="1"
            defaultValue={formData.quantity}
            key={`qty-${formData.quantity === 0 ? 'init' : ''}`}
            onChange={(e) => { const v = parseInt(e.target.value); if (!isNaN(v)) handleInputChange('quantity', v); }}
            onBlur={(e) => { const v = parseInt(e.target.value); const c = isNaN(v) || v < 1 ? 1 : v; handleInputChange('quantity', c); e.target.value = String(c); }}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.quantity ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter quantity"
          />
          {errors.quantity && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <AlertCircle className="h-4 w-4 mr-1" />
              {errors.quantity}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Days Supply <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min="1"
            defaultValue={formData.daysSupply}
            key={`days-${formData.daysSupply === 0 ? 'init' : ''}`}
            onChange={(e) => { const v = parseInt(e.target.value); if (!isNaN(v)) handleInputChange('daysSupply', v); }}
            onBlur={(e) => { const v = parseInt(e.target.value); const c = isNaN(v) || v < 1 ? 1 : v; handleInputChange('daysSupply', c); e.target.value = String(c); }}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errors.daysSupply ? 'border-red-500' : 'border-gray-300'
            }`}
            placeholder="Enter days supply"
          />
          {errors.daysSupply && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <AlertCircle className="h-4 w-4 mr-1" />
              {errors.daysSupply}
            </p>
          )}
        </div>
      </div>

      {/* Pharmacy Notes */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Notes to Pharmacy
        </label>
        <textarea
          value={formData.pharmacyNotes}
          onChange={(e) => handleInputChange('pharmacyNotes', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
          rows={3}
          placeholder="Additional notes for the pharmacy (optional)"
        />
        <p className="mt-1 text-sm text-gray-500">
          These notes will be sent to the pharmacy with the prescription
        </p>
      </div>

      {/* Action Buttons - Only show when in modal mode */}
      {asModal && (
        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors flex items-center"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Save Prescription
          </button>
        </div>
      )}
    </form>
  );

  // Render as modal or regular component based on asModal prop
  if (asModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              Prescription Details
            </h2>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-6">
            {formContent}
          </div>
        </div>
      </div>
    );
  }

  // Render as regular component without modal wrapper
  return formContent;
}; 